import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, MapPin, Package, Truck } from "lucide-react";
import { api, fmtDate, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusChip } from "@/components/account/StatusChip";
import { Reveal } from "@/components/Reveal";

const Line = ({ label, value, bold }) => (
  <div className={`flex justify-between text-sm ${bold ? "font-heading text-base font-bold" : "text-neutral-600"}`}>
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

export default function OrderDetailPage() {
  const { id } = useParams();
  const { getToken, loading: authLoading, isLoggedIn } = useAuth();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (authLoading) return;
    (async () => {
      const token = await getToken();
      if (!token) {
        setError("Please sign in to view this order.");
        return;
      }
      try {
        setOrder(await api.authOrder(id, token));
      } catch (e) {
        setError(e?.response?.data?.detail || "We couldn't load this order.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, authLoading, isLoggedIn]);

  if (error) {
    return (
      <main className="mx-auto max-w-md px-4 py-32 text-center" data-testid="order-detail-error">
        <h1 className="font-heading text-xl font-bold">Order unavailable</h1>
        <p className="mt-2 text-sm text-neutral-500">{error}</p>
        <Link to="/account" className="mt-6 inline-block rounded-full bg-neutral-900 px-6 py-3 text-sm font-semibold text-white">
          Back to Account
        </Link>
      </main>
    );
  }

  if (!order) {
    return (
      <main className="mx-auto max-w-4xl px-4 py-24" data-testid="order-detail-loading">
        <div className="h-72 animate-pulse rounded-2xl bg-neutral-200/70" />
      </main>
    );
  }

  const cur = order.currency;

  return (
    <main className="mx-auto max-w-4xl px-4 py-10 sm:px-6 sm:py-14" data-testid="order-detail-page">
      <Reveal>
        <Link
          to="/account?tab=orders"
          data-testid="order-detail-back"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-500 transition-colors hover:text-neutral-900"
        >
          <ArrowLeft size={13} /> Back to orders
        </Link>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-neutral-400">Order</p>
            <h1 className="mt-1 font-heading text-2xl font-bold tracking-tight sm:text-3xl" data-testid="order-detail-number">
              {order.name || `#${order.order_number}`}
            </h1>
            <p className="mt-1 text-sm text-neutral-500">
              Placed {fmtDate(order.date)}
              {order.confirmation_number ? ` · Confirmation ${order.confirmation_number}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <StatusChip status={order.fulfillment_status} testId="order-detail-fulfillment" />
            <StatusChip status={order.payment_status} testId="order-detail-payment" />
          </div>
        </div>
      </Reveal>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_320px]">
        <Reveal delay={0.06}>
          <section className="rounded-2xl border border-neutral-200 bg-white" data-testid="order-detail-items">
            <h2 className="border-b border-neutral-100 px-6 py-4 font-heading text-base font-bold">Items</h2>
            <ul className="divide-y divide-neutral-100">
              {order.items.map((i) => (
                <li key={i.id} className="flex items-center gap-4 px-6 py-4">
                  {i.image ? (
                    <img src={i.image} alt={i.title} className="h-16 w-16 rounded-xl bg-neutral-100 object-cover" />
                  ) : (
                    <div className="grid h-16 w-16 place-items-center rounded-xl bg-neutral-100">
                      <Package size={18} className="text-neutral-400" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-neutral-900">{i.title}</p>
                    <p className="text-xs text-neutral-500">
                      {i.variant && i.variant !== "Default Title" ? `${i.variant} · ` : ""}Qty {i.qty}
                    </p>
                  </div>
                  <p className="font-heading text-sm font-bold">{money(i.line_total ?? i.unit_price * i.qty, cur)}</p>
                </li>
              ))}
            </ul>
          </section>

          {order.fulfillments?.length > 0 && (
            <section className="mt-6 rounded-2xl border border-neutral-200 bg-white p-6" data-testid="order-detail-shipments">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold">
                <Truck size={16} /> Shipments
              </h2>
              <ul className="mt-4 space-y-4">
                {order.fulfillments.map((f, idx) => (
                  <li key={idx} className="rounded-xl bg-neutral-50 p-4 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusChip status={f.status} />
                      {f.shipment_status && <span className="text-xs text-neutral-500">{f.shipment_status}</span>}
                    </div>
                    {f.estimated_delivery && (
                      <p className="mt-2 text-xs text-neutral-500">Estimated delivery {fmtDate(f.estimated_delivery)}</p>
                    )}
                    {f.tracking.map((t, ti) => (
                      <p key={ti} className="mt-2 text-xs text-neutral-700">
                        {t.company ? `${t.company} · ` : ""}
                        {t.url ? (
                          <a href={t.url} target="_blank" rel="noreferrer" className="font-semibold underline underline-offset-4">
                            Track {t.number}
                          </a>
                        ) : (
                          t.number
                        )}
                      </p>
                    ))}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </Reveal>

        <Reveal delay={0.12}>
          <aside className="space-y-6">
            <section className="rounded-2xl border border-neutral-200 bg-white p-6 space-y-2" data-testid="order-detail-summary">
              <h2 className="mb-3 font-heading text-base font-bold">Summary</h2>
              {order.subtotal != null && <Line label="Subtotal" value={money(order.subtotal, cur)} />}
              {order.shipping != null && <Line label={order.shipping_method ? `Shipping · ${order.shipping_method}` : "Shipping"} value={order.shipping === 0 ? "Free" : money(order.shipping, cur)} />}
              {order.tax != null && order.tax > 0 && <Line label="Tax" value={money(order.tax, cur)} />}
              {order.refunded > 0 && <Line label="Refunded" value={`− ${money(order.refunded, cur)}`} />}
              <div className="border-t border-neutral-100 pt-3">
                <Line label="Total" value={money(order.total, cur)} bold />
              </div>
            </section>

            {order.shipping_address?.length > 0 && (
              <section className="rounded-2xl border border-neutral-200 bg-white p-6" data-testid="order-detail-address">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold">
                  <MapPin size={15} /> Delivery address
                </h2>
                <address className="mt-3 text-sm not-italic leading-relaxed text-neutral-600">
                  {order.shipping_address.map((l, i) => (
                    <span key={i} className="block">
                      {l}
                    </span>
                  ))}
                </address>
              </section>
            )}

            {order.status_page_url && (
              <a
                href={order.status_page_url}
                target="_blank"
                rel="noreferrer"
                data-testid="order-detail-status-link"
                className="flex items-center justify-center gap-2 rounded-full border border-neutral-300 py-3 text-sm font-semibold transition-colors hover:border-neutral-900"
              >
                Open Shopify order status <ExternalLink size={14} />
              </a>
            )}
          </aside>
        </Reveal>
      </div>
    </main>
  );
}
